import React, { useState, useCallback } from "react";
import {
  Collapse,
  Card,
  Button,
  CardBody,
  Row,
  Col,
  Alert,
  Badge,
  Spinner,
} from "reactstrap";
import PieChart from "../../Components/Common/PieChart";
import PaymentAccordion from "../../Components/Common/PaymentAccordion";
import { useNavigate } from "react-router-dom";

const ClientContracts = ({ contracts }) => {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(null);

  const handleToggleAccordion = useCallback(
    (index) => {
      setActiveIndex(activeIndex === index ? null : index);
    },
    [activeIndex]
  );
  
  const handleViewContract = useCallback((contractId, e) => {
    e.stopPropagation(); // Prevent accordion toggle
    navigate(`/contracts/${contractId}`);
  }, [navigate]);

  const ContractChart = ({ aggregatedPayments }) => {
    if (!aggregatedPayments?.payments?.length) {
      return (
        <div className="text-center p-4">
          <i className="ri-pie-chart-line fs-1 text-muted mb-2 d-block"></i>
          <p className="text-muted small">No payment data available</p>
        </div>
      );
    }

    return (
      <div className="w-100 h-100 position-relative">
        <PieChart
          payments={aggregatedPayments.payments}
          paymentAmounts={aggregatedPayments.payments.map(p => p.amount)}
          settlementBalance={aggregatedPayments.payments.reduce((sum, p) => sum + p.amount, 0)}
        />
      </div>
    );
  };

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "N/A";
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid Date";
    }
  }, []);

  const formatCurrency = useCallback((amount) => {
    if (!amount) return "€0";
    return new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }, []);

  const getContractStatusBadge = (contract) => {
    const totalValue = contract.total_contract_value || 0;
    const totalCashed = contract.total_cashed || 0;
    const progress = totalValue > 0 ? (totalCashed / totalValue) * 100 : 0;

    if (progress === 0) {
      return { color: "secondary", text: "Not Started", variant: "outline" };
    } else if (progress < 100) {
      return { color: "warning", text: "In Progress", variant: "outline" };
    } else {
      return { color: "success", text: "Completed", variant: "solid" };
    }
  };

  if (!contracts || contracts.length === 0) {
    return (
      <div className="text-center p-5">
        <div className="mb-4">
          <div
            className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center"
            style={{ width: "80px", height: "80px" }}
          >
            <i className="ri-file-list-3-line fs-2 text-muted"></i>
          </div>
        </div>
        <h5 className="mb-2">No Contracts Available</h5>
        <p className="text-muted mb-0">
          There are currently no contracts associated with this client.
        </p>
      </div>
    );
  }

  return (
    <div className="contract-list">
      {contracts.map((contract, index) => {
        const statusBadge = getContractStatusBadge(contract);
        const hasPayments = contract.aggregated_payments?.payments?.length > 0;
        const isExpanded = activeIndex === index;

        return (
          <Card
            key={contract.id}
            className="border-0 shadow-sm mb-4 contract-card"
            style={{ borderRadius: "12px", overflow: "hidden" }}
          >
            <CardBody className="p-0">
              {/* Contract Header */}
              <div
                className="contract-header d-flex align-items-center justify-content-between p-3 border-bottom cursor-pointer"
                onClick={() => handleToggleAccordion(index)}
                style={{
                  background: isExpanded ? "rgba(75, 56, 179, 0.05)" : "white",
                  transition: "background-color 0.3s",
                }}
              >
                <div className="d-flex align-items-center flex-grow-1">
                  <div
                    className="contract-icon me-3 rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: "42px",
                      height: "42px",
                      backgroundColor: "rgba(75, 56, 179, 0.1)",
                    }}
                  >
                    <i
                      className="ri-file-text-line fs-4"
                      style={{ color: "#4b38b3" }}
                    ></i>
                  </div>
                  
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center mb-1">
                      <h5 className="mb-0 fw-semibold me-2">
                        Contract #{contract.pvc_number}
                      </h5>
                      <Badge
                        color={statusBadge.color}
                        className="px-2 py-1"
                        style={{ fontSize: '0.75rem' }}
                      >
                        {statusBadge.text}
                      </Badge>
                    </div>
                    
                    <div className="d-flex align-items-center text-muted small">
                      <span className="me-3">
                        <i className="ri-calendar-line me-1"></i>
                        {formatDate(contract.pvc_date)}
                      </span>
                      {contract.total_contract_value && (
                        <span>
                          <i className="ri-money-euro-circle-line me-1"></i>
                          {formatCurrency(contract.total_contract_value)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="d-flex align-items-center">
                  <Button
                    color="primary"
                    size="sm"
                    onClick={(e) => handleViewContract(contract.id, e)}
                    className="d-inline-flex align-items-center me-3"
                    style={{ borderRadius: "8px" }}
                  >
                    <i className="ri-external-link-line me-1"></i>
                    View Details
                  </Button>
                  
                  <span
                    className={`contract-toggle-icon ${isExpanded ? "expanded" : ""}`}
                  >
                    <i
                      className={`ri-arrow-down-s-line fs-4 transition-transform ${
                        isExpanded ? "rotate-180" : ""
                      }`}
                    ></i>
                  </span>
                </div>
              </div>

              {/* Contract Details */}
              <Collapse isOpen={isExpanded}>
                <div className="contract-details p-4">
                  {hasPayments ? (
                    <Row className="g-4">
                      <Col lg={7}>
                        <Card
                          className="border h-100 dashboard-card"
                          style={{ borderRadius: "12px" }}
                        >
                          <div className="card-header bg-transparent border-bottom p-3">
                            <h6 className="mb-0 text-dark fw-semibold">
                              <i className="ri-money-dollar-circle-line me-2"></i>
                              Payment Schedule
                            </h6>
                          </div>
                          <CardBody className="p-0">
                            <PaymentAccordion
                              payments={contract.aggregated_payments.payments}
                              aggregated={true}
                            />
                          </CardBody>
                        </Card>
                      </Col>
                      
                      <Col lg={5}>
                        <Card
                          className="border h-100 dashboard-card"
                          style={{ borderRadius: "12px" }}
                        >
                          <div className="card-header bg-transparent border-bottom p-3">
                            <h6 className="mb-0 text-dark fw-semibold">
                              <i className="ri-pie-chart-line me-2"></i>
                              Payment Distribution
                            </h6>
                          </div>
                          <CardBody className="d-flex flex-column">
                            <div className="flex-grow-1 d-flex align-items-center justify-content-center p-2">
                              <ContractChart aggregatedPayments={contract.aggregated_payments} />
                            </div>
                            
                            {/* Payment Summary */}
                            <div className="mt-3 pt-3 border-top">
                              <div className="row text-center">
                                <div className="col-6">
                                  <div className="text-muted small">Total Value</div>
                                  <div className="fw-bold text-primary">
                                    {formatCurrency(contract.total_contract_value)}
                                  </div>
                                </div>
                                <div className="col-6">
                                  <div className="text-muted small">Total Cashed</div>
                                  <div className="fw-bold text-success">
                                    {formatCurrency(contract.total_cashed)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                      </Col>
                    </Row>
                  ) : (
                    <Alert color="info" className="mb-0" fade={false}>
                      <div className="d-flex align-items-center">
                        <i className="ri-information-line me-2 fs-4"></i>
                        <div>
                          <h6 className="mb-1">No Payment Data</h6>
                          <p className="mb-0">
                            There are no settlement prices or payments associated
                            with this contract yet.
                          </p>
                        </div>
                      </div>
                    </Alert>
                  )}
                </div>
              </Collapse>
            </CardBody>
          </Card>
        );
      })}

      <style jsx>{`
        .contract-card {
          transition: all 0.3s ease;
        }
        
        .contract-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.1) !important;
        }
        
        .contract-header {
          cursor: pointer;
        }
        
        .contract-toggle-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background-color: #f8f9fa;
          transition: all 0.3s ease;
        }
        
        .contract-toggle-icon.expanded {
          background-color: rgba(75, 56, 179, 0.1);
        }
        
        .rotate-180 {
          transform: rotate(180deg);
        }
        
        .transition-transform {
          transition: transform 0.3s ease;
        }
        
        .dashboard-card {
          border-radius: 12px !important;
          overflow: hidden;
          box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
          height: 100%;
        }
        
        .cursor-pointer {
          cursor: pointer;
        }

        .card-header {
          background: #f8f9fa !important;
        }
      `}</style>
    </div>
  );
};

export default ClientContracts;